import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './use-auth';
import { ApiError } from './api';
import { fetchMyTutorProfile } from './tutor-api';
import type { TutorProfileResponse } from './schemas/tutor-profile';

type TutorProfileCtx = {
  profile: TutorProfileResponse | null;
  loading: boolean;
  error: string | null;
  updateProfile: (p: TutorProfileResponse) => void;
};

const TutorProfileContext = createContext<TutorProfileCtx | null>(null);

export function TutorProfileProvider({ children }: { children: React.ReactNode }) {
  const { accessToken } = useAuth();
  const [profile, setProfile] = useState<TutorProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!accessToken) { setLoading(false); return; }
      try {
        const p = await fetchMyTutorProfile(accessToken);
        if (!cancelled) { setProfile(p); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken]);

  const updateProfile = useCallback((p: TutorProfileResponse) => setProfile(p), []);

  return (
    <TutorProfileContext.Provider value={{ profile, loading, error, updateProfile }}>
      {children}
    </TutorProfileContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTutorProfile(): TutorProfileCtx | null {
  return useContext(TutorProfileContext);
}
